import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { LogOnMount } from '@mightyhive/analytics';
import {
  AppGradientSurface,
  ConfirmDialog,
  EntranceReveal,
  WelcomeHeader,
} from '@mightyhive/commoncomponents';
import { APP_LAYOUT_SIZES } from '@mightyhive/theme';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { RouteLocations } from './RouteLocations';
import { apiFetch } from '../utils/api';
import { formatExperimentCreatedDate } from '../utils/geoliftLabels';
import type { LibraryExperiment, LibraryListResponse } from '../utils/libraryTypes';
import { LibraryExperimentCard } from './library/components/LibraryExperimentCard';
import { LibraryFilterToolbar } from './library/components/LibraryFilterToolbar';
import { LibraryPageHeader } from './library/components/LibraryPageHeader';
import { LibraryStatusFilterBar } from './library/components/LibraryStatusFilterBar';
import {
  type LibrarySortOrder,
  type LibraryStatusFilterId,
  resolveLibraryStatusFilter,
} from './library/libraryStatus';
import { LIBRARY_ENTRANCE_DELAYS, LIBRARY_PAGE_LAYOUT_SIZES } from './library/libraryTokens';

const LIBRARY_HERO_HEADING = 'What would you like to continue?';
const LIBRARY_HERO_SUBTITLE = 'Design, plan, and analysis — all in one place.';
const LIBRARY_SECTION_SUBTITLE = 'All experiments across all stages.';

function buildStatusCounts(experiments: LibraryExperiment[]): Record<LibraryStatusFilterId, number> {
  const counts: Record<LibraryStatusFilterId, number> = {
    all: experiments.length,
    draft: 0,
    design: 0,
    analysis: 0,
    complete: 0,
  };

  for (const experiment of experiments) {
    const bucket = resolveLibraryStatusFilter(experiment.status_label);
    counts[bucket] += 1;
  }

  return counts;
}

export function Library(): JSX.Element {
  const navigate = useNavigate();
  const { selectedClientExternalUuid, loading: authLoading } = useUser();

  const [experiments, setExperiments] = useState<LibraryExperiment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<LibraryStatusFilterId>('all');
  const [sortOrder, setSortOrder] = useState<LibrarySortOrder>('newest');
  const [deleteTarget, setDeleteTarget] = useState<LibraryExperiment | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!selectedClientExternalUuid) {
      setExperiments([]);
      setLoading(false);
      setError(null);
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    apiFetch(
      `/api/experiments/?client_external_uuid=${encodeURIComponent(selectedClientExternalUuid)}`,
    )
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((data: LibraryListResponse) => {
        if (!active) return;
        setExperiments(data.data?.experiments ?? []);
      })
      .catch((status: unknown) => {
        if (!active) return;
        if (status === 401 || status === 403) {
          window.location.href = `/login?next=${encodeURIComponent(window.location.href)}`;
          return;
        }
        setError('Failed to load experiments.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [authLoading, selectedClientExternalUuid]);

  const statusCounts = useMemo(() => buildStatusCounts(experiments), [experiments]);

  const filteredExperiments = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    const filtered = experiments.filter((experiment) => {
      const matchesStatus =
        statusFilter === 'all' ||
        resolveLibraryStatusFilter(experiment.status_label) === statusFilter;
      if (!matchesStatus) return false;

      if (!query) return true;
      const haystack = `${experiment.name} ${experiment.description}`.toLowerCase();
      return haystack.includes(query);
    });

    return [...filtered].sort((left, right) => {
      const leftTime = new Date(left.created_at).getTime();
      const rightTime = new Date(right.created_at).getTime();
      return sortOrder === 'newest' ? rightTime - leftTime : leftTime - rightTime;
    });
  }, [experiments, searchQuery, sortOrder, statusFilter]);

  async function confirmDelete(): Promise<void> {
    if (!deleteTarget) return;
    const experiment = deleteTarget;
    setDeleting(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/experiments/${experiment.external_uuid}/`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const errJson = (await res.json().catch(() => ({}))) as { status_message?: string };
        throw new Error(errJson.status_message ?? `Delete failed (${res.status})`);
      }
      setExperiments((prev) =>
        prev.filter((row) => row.external_uuid !== experiment.external_uuid),
      );
      setDeleteTarget(null);
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Failed to delete experiment');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <AppGradientSurface maxWidth={APP_LAYOUT_SIZES.libraryContentMaxWidth}>
      <LogOnMount eventName="Page View: Library" />

      <WelcomeHeader
        animateEntrance
        hideGreeting
        heading={LIBRARY_HERO_HEADING}
        subtitle={LIBRARY_HERO_SUBTITLE}
        maxWidth={APP_LAYOUT_SIZES.libraryContentMaxWidth}
        entranceDelays={{
          heading: LIBRARY_ENTRANCE_DELAYS.heading,
          subtitle: LIBRARY_ENTRANCE_DELAYS.heroSubtitle,
        }}
      />

      <EntranceReveal delayMs={LIBRARY_ENTRANCE_DELAYS.sectionTitle}>
        <LibraryPageHeader subtitle={LIBRARY_SECTION_SUBTITLE} />
      </EntranceReveal>

      <Box sx={{ mt: `${LIBRARY_PAGE_LAYOUT_SIZES.sectionToFiltersGap}px` }}>
        <EntranceReveal delayMs={LIBRARY_ENTRANCE_DELAYS.statusFilters}>
          <LibraryStatusFilterBar
            activeFilter={statusFilter}
            counts={statusCounts}
            onChange={setStatusFilter}
          />
        </EntranceReveal>

        <Box sx={{ mt: `${LIBRARY_PAGE_LAYOUT_SIZES.filterBarToToolbarGap}px` }}>
          <EntranceReveal delayMs={LIBRARY_ENTRANCE_DELAYS.toolbar}>
            <LibraryFilterToolbar
              searchQuery={searchQuery}
              sortOrder={sortOrder}
              onSearchChange={setSearchQuery}
              onSortChange={setSortOrder}
              onNewExperiment={() => navigate(`${RouteLocations.GRID}/create-experiment`)}
            />
          </EntranceReveal>
        </Box>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      ) : null}

      {!loading && !selectedClientExternalUuid ? (
        <Typography variant="body2" sx={{ mt: 4, color: 'text.secondary' }}>
          Select a client to view experiments.
        </Typography>
      ) : null}

      {!loading && selectedClientExternalUuid ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
            gap: `${LIBRARY_PAGE_LAYOUT_SIZES.cardGridGap}px`,
            mt: 3,
            alignItems: 'stretch',
          }}
        >
          {filteredExperiments.map((experiment, index) => {
            const cardDescription = experiment.description.trim() || undefined;

            return (
              <EntranceReveal
                key={experiment.external_uuid}
                delayMs={
                  LIBRARY_ENTRANCE_DELAYS.cardBase +
                  index * LIBRARY_ENTRANCE_DELAYS.cardStagger
                }
              >
                <LibraryExperimentCard
                  title={experiment.name}
                  description={cardDescription}
                  typeLabel={experiment.type_label}
                  statusLabel={experiment.status_label}
                  owner={experiment.owner}
                  createdAtLabel={formatExperimentCreatedDate(experiment.created_at)}
                  onClick={() =>
                    navigate(`${RouteLocations.LIBRARY}/${experiment.external_uuid}`)
                  }
                  showDelete={experiment.can_delete !== false}
                  onDelete={() => setDeleteTarget(experiment)}
                />
              </EntranceReveal>
            );
          })}
        </Box>
      ) : null}

      {!loading && selectedClientExternalUuid && filteredExperiments.length === 0 && experiments.length > 0 ? (
        <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary' }}>
          No experiments match your filters.
        </Typography>
      ) : null}

      {!loading && selectedClientExternalUuid && experiments.length === 0 ? (
        <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary' }}>
          No experiments yet. Create one to get started.
        </Typography>
      ) : null}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete experiment"
        message={
          deleteTarget
            ? `Are you sure you want to delete "${deleteTarget.name}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        destructive
        loading={deleting}
        onCancel={() => {
          if (!deleting) setDeleteTarget(null);
        }}
        onConfirm={() => void confirmDelete()}
      />
    </AppGradientSurface>
  );
}
